import { useEffect, useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type Compare,
  useChartState,
} from "@features/sales-v3/hooks/use-chart-state";
import { type SalesPageMetric } from "@north-beam/nb-client-side";

import { CompareMenu } from "./compare-menu";

const MAX_NUMBER_OF_COMPARE_METRICS = 2;

interface CompareMenusProps {
  metrics: SalesPageMetric[];
  comparisons: Compare[];
  chartIndex: number;
  isBarChart: boolean;
  disabled?: boolean;
}

const comparePlaceholder = {
  queryKey: "",
  queryValue: "",
  filters: [],
} as unknown as Compare;

export const CompareMenus = ({
  metrics,
  comparisons,
  chartIndex,
  isBarChart,
  disabled = false,
}: CompareMenusProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    chartConfigs,
    addCompareToChartAt,
    deleteCompareFromChartAt,
    updateCompareFromChartAt,
  } = useChartState();
  const [compareToAddPlaceholder, setCompareToAddPlaceholder] =
    useState<Compare>({ ...comparePlaceholder });

  const updateCompare = (compareIndex: number) => (compare: Compare) => {
    prettyLogEventPage({
      domain: "Charts",
      action: "Other",
      additionalActionInfo: "Update Compare Menu",
      data: {
        pageContext: { compare },
      },
    });

    updateCompareFromChartAt({ chartIndex, compareIndex }, compare);
  };

  const deleteCompare = (compareIndex: number) => () => {
    prettyLogEventPage({
      domain: "Charts",
      action: "Other",
      additionalActionInfo: "Delete Compare Menu",
    });

    deleteCompareFromChartAt({ chartIndex, compareIndex });
  };

  useEffect(() => {
    if (compareToAddPlaceholder?.queryKey) {
      addCompareToChartAt(chartIndex, compareToAddPlaceholder);
      setCompareToAddPlaceholder({ ...comparePlaceholder });
    }
  }, [compareToAddPlaceholder, addCompareToChartAt, chartIndex]);

  const canAddCompare =
    (comparisons.length < MAX_NUMBER_OF_COMPARE_METRICS &&
      comparisons[0]?.queryKey === "metricName") ||
    comparisons.length === 0;

  const isSecondComparison = comparisons.length === 1;

  return (
    <>
      {comparisons.map((compare, compareIndex) => (
        <div key={compareIndex} className="flex flex-col items-start">
          <CompareMenu
            metrics={metrics}
            updateCompare={updateCompare(compareIndex)}
            deleteCompare={deleteCompare(compareIndex)}
            compare={compare}
            compareIndex={compareIndex}
            isBarChart={isBarChart}
            disabled={disabled}
          />
        </div>
      ))}
      {canAddCompare && (
        <div className="flex flex-col items-start">
          <CompareMenu
            metrics={metrics}
            updateCompare={(compare) => {
              prettyLogEventPage({
                domain: "Charts",
                action: "Other",
                additionalActionInfo: "Update Compare Menu",
                data: {
                  pageContext: { compare },
                },
              });

              const mainMetric = chartConfigs[chartIndex].mainMetric;

              setCompareToAddPlaceholder({
                ...compare,
                filters: [...mainMetric.filters],
                ignoreTableFilters: mainMetric.ignoreTableFilters,
              });
            }}
            deleteCompare={() => {
              prettyLogEventPage({
                domain: "Charts",
                action: "Other",
                additionalActionInfo: "Delete Compare Menu",
              });

              setCompareToAddPlaceholder({ ...comparePlaceholder });
            }}
            compare={compareToAddPlaceholder}
            isBarChart={isBarChart}
            isSecondComparison={isSecondComparison}
            disabled={disabled}
          />
        </div>
      )}
    </>
  );
};
